/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {cloneDeep, uniq} from 'lodash';
import {entityId} from '../services/ConnectUtils';
import {
  Account,
  AccountLink,
  GenericObject,
  SubAccountLink,
  User
} from '../types/entities';
import {CheckboxState} from '../types/general';

export interface AccountHashMapType {
  title?: string;
  checked?: CheckboxState;
  subAccounts?: any[],
  parentId?: any;
  siblings?: any[];
  crumb?: any[];
  crumbRaw?: {
    [key: string]: string;
  }[];
  id?: string | number;
}

export interface AccountHashMapKeyType {
  [key: string]: AccountHashMapType
}

/**
 * Provides a utility for retrieving an account by `id` from within a hierarchy of
 * `AccountLinks`.
 *
 * This function should be used exclusively for retrieving an individual account by
 * `id` from within a given account hierarchy unless the account hierarchy has already
 * been flattened via the `toAccountIdMap` utility, in which case the map should be
 * used so as to provide immediate references by id.**
 *
 * ```typescript
 *   // account hierarchy ...
 *   const accounts = [{
 *     title       : 'Parent',
 *     href        : 'http://connect.calamp.com/accounts/10',
 *     subAccounts : [{
 *       title       : 'Child',
 *       href        : 'http://connect.calamp.com/accounts/20',
 *       subAccounts : [{
 *         title       : 'Grandchild',
 *         href        : 'http://connect.calamp.com/accounts/30',
 *         subAccounts : {
 *           title       : 'Great Grandchild',
 *           href: 'http://connect.calamp.com/accounts/40'
 *         }
 *       }]
 *     }]
 *   }];
 *
 *   getAccountById(accounts, 10).title; // 'Parent'
 *   getAccountById(accounts, 20).title; // 'Child'
 *   getAccountById(accounts, 30).title; // 'Grandchild'
 *   getAccountById(accounts, 40).title; // 'Great Grandchild'
 * ```
 *
 * @param   accounts An array of `AccountLink` instances which make up the `account`
 *          hierarchy from which to search.
 * @param   id The `id` of the account which is to be returned from within the hierarchy.
 *          Note, while the account hierarchy will consist of `AccountLink` instances,
 *          whereby only the account `href` will be available, the `id` will be parsed
 *          accordingly and thus properly matched with the given id.
 * @returns Either the `AccountLink` for the given account id within the hierarchy, if
 *          it is present, otherwise `null`.
 */
export const getAccountById = (accounts: AccountLink[], id: number): AccountLink | null => {
  const stack = [...accounts];
  while (stack.length > 0) {
    const account = stack.shift();
    if (account) {
      if (entityId(account) !== id) {
        const {subAccounts} = account;
        if (subAccounts) {
          const n = subAccounts.length;
          for (let i = 0; i < n; i++) {
            const child = subAccounts[i];
            stack.push(child?.subaccount || child);
          }
        }
      } else {
        return account;
      }
    }
  }
  return null;
};

/**
 * **Note: For retrieving an individual account by id from within a given account hierarchy,
 * the `getAccountById` utility should be used. The `toAccountIdMap` utility should generally
 * be used during initialization of an account hierarchy for generating a mapping of account
 * ids to account links so as to allow for immediate subsequent lookups.**
 *
 * Provides a utility which iterates through an account hierarchy and returns a map where each
 * account id is a key and the account is the value. This function can be used to flatten an
 * entire account hierarchy immediately upon retrieving the users account hierarchy, and used
 * subsequently for immediate access of any account within the hierarchy. Alternatively, any
 * value can be used for the account ids if the account itself is not necessary as the value.
 *
 * ```typescript
 *   import {toAccountMap} from '@ion/core/src/entities/Accounts';
 *
 *   const toAccountIdMap(accounts); // map of all account ids as keys and accounts as values
 *   const toAccountIdMap(accounts, true); // map of all account ids as keys and true values
 * ```
 *
 * @param   {AccountLink[]} accounts A collection of account links to
 * @param   {any} value An optional value to use for each entry, otherwise the entry itself
 *          will be used.
 * @param   {GenericObject} map The mapping of account ids.
 * @returns {GenericObject} An object containing all account ids as key for the entire hierarchy.
 */
export const toAccountIdMap = (
  accounts: AccountLink[], value = null, map: GenericObject = {}): GenericObject => {
  accounts?.forEach((account: AccountLink) => {
    const {subAccounts} = account;
    map[entityId(account)] = value || account;
    if (subAccounts?.length) {
      toAccountIdMap(subAccounts.map((sub: SubAccountLink) => sanitizeAccount(sub)), value, map);
    }
  });
  return map;
};

/**
 * @setAccountTree
 *
 * Takes a deeply nested account object with an array of accounts
 * and flattens the data to a single level object literal with each
 * account's id (parsed from a HATEOS link) as the key.
 *
 * @returns @type @AccountHashMapKeyType
 *
 * ```typescript
 *   import {setAccountTree} from "@ion/core/src/entities/Account";
 *   const bloatedAccountObject = {
 *     accounts : [
 *      ...
 *     ]
 *   }
 *
 *   const accountTree = setAccountTree(bloatedAccountObject);
 *   // {1234: {}, 4321: {}, ...}
 *
 * ```
 *
 */
export function setAccountTree(accountData, verbose: boolean = false): AccountHashMapKeyType {
  const checkboxHashMap: AccountHashMapKeyType = {};
  const topLevelSiblings = [];

  function setSubAccountList(subAccounts) {
    if (!subAccounts) return null;
    const list = [];
    subAccounts.forEach(account => {
      list.push(verbose ? sanitizeAccount(account) : entityId(sanitizeAccount(account)?.href));
    });
    return list;
  }

  function setCheckboxHashItem(item, parent) {
    const id = entityId(item.href);
    const parentId = entityId(parent.href);
    checkboxHashMap[id] = {
      href        : item.href,
      title       : item.title,
      checked     : CheckboxState.Unchecked,
      subAccounts : setSubAccountList(item?.subAccounts),
      parentId    : verbose ? parent : parentId,
      ...(verbose && item)
    };
  }

  function checkSubAccount(parentAccount) {
    parentAccount.subAccounts.forEach(sub => {
      const sanitizedSub = sanitizeAccount(sub);
      setCheckboxHashItem(sanitizedSub, parentAccount);
      if (sanitizedSub?.subAccounts) {
        checkSubAccount(sanitizedSub);
      }
    });
  };

  accountData?.forEach(account => {
    const sanitizedAccount = sanitizeAccount(account);
    const id = entityId(sanitizedAccount?.href);
    checkboxHashMap[id] = {
      href        : sanitizedAccount?.href,
      title       : sanitizedAccount?.title,
      checked     : CheckboxState.Unchecked,
      subAccounts : setSubAccountList(sanitizedAccount?.subAccounts),
      parentId    : null,
      ...(verbose && account)
    };
    if (sanitizedAccount?.subAccounts) {
      checkSubAccount(sanitizedAccount);
    }
    topLevelSiblings.push(id);
  });
  return checkboxHashMap;
}

/**
 * @updateTreeCheckedState
 *
 * This function takes an AccountHashMapKeyType tree and traverses it
 * to update the entire tree to it's appropriate checked state based on
 * an checkbox that was engaged with
 *
 * @param string
 * @param CheckboxState
 * @param AccountHashMapKeyType
 *
 * ```typescript
 *   import {updateTreeCheckedState} from "@ion/core/src/entities/Account";
 *
 *   updateCheckboxTreeState('1234', CheckboxState.Checked, accountHashMap);
 *
 * ```
 */
export function updateCheckboxTreeState(
  itemId: string,
  checked: CheckboxState,
  accountMap: AccountHashMapKeyType
) {
  // at minimum this value is set to what is passed
  const checkBoxMap = cloneDeep(accountMap);
  const engagedItem = checkBoxMap[itemId];
  engagedItem.checked = checked;
  // fire off checks up and down stream to update the tree
  return downstreamTreeCheck(itemId, checked, checkBoxMap);
};

/**
 * @getAccountKey
 *
 * This function takes a SelectedAccounts tree and AccountName
 * to update the entire SelectedAccounts to it's appropriate checked state.
 *
 * @param AccountSelections
 * @param AccountFilterName
 *
 * ```typescript
 *   import {getAccountKey} from "@ion/core/src/entities/Account";
 *
 *   getAccountKey(selectedAccounts, selected);
 *
 * ```
 */
export const getAccountKey = (selectedAccounts: GenericObject, selected: string): string => {
  return Object.keys(selectedAccounts).filter(key => {
    const item = selectedAccounts[key];
    return item?.checked && item?.title === selected;
  })[0];
};

/**
 * @upstreamTreeCheck
 *
 * Handles all upstream (parents) tree checking for an account map and updates
 * the appropriate checkbox state based on the state of any of the children. Called
 * recursively depending on how many levels of parents there are.
 *
 * @param AccountHashMapType
 * @param AccountHashMapKeyType
 *
 * ```typescript
 *   import {upstreamTreeCheck} from "@ion/core/src/entities/Account";
 *
 *   upstreamTreeCheck(item, checkboxMap);
 *
 * ```
 */
export function upstreamTreeCheck(
  item: AccountHashMapType,
  checkBoxMap: AccountHashMapKeyType
) {
  const parentId = item?.parentId;
  if (parentId) {
    const siblingAccounts = checkBoxMap[parentId].subAccounts;
    const siblingAccountsLength = siblingAccounts?.length ?? 0;
    if (siblingAccountsLength) {
      const checkedSiblings = siblingAccounts?.filter(siblingId => {
        return checkBoxMap[siblingId].checked === CheckboxState.Checked;
      });
      const indeterminateSiblings = siblingAccounts?.filter(siblingId => {
        return checkBoxMap[siblingId].checked === CheckboxState.Indeterminate;
      });
      switch (item.checked) {
        case CheckboxState.Checked:
          if (checkedSiblings.length === siblingAccountsLength) {
          // if all the siblings are checked then the parent should be checked
            checkBoxMap[parentId].checked = CheckboxState.Checked;
          } else {
          // otherwise the parent is indeterminate (cannot be unchecked)
            checkBoxMap[parentId].checked = CheckboxState.Indeterminate;
          }
          break;
        case CheckboxState.Unchecked:
          if (!checkedSiblings.length && !indeterminateSiblings.length) {
          // if the item and all of its siblings are unchecked then the parent is unchecked
            checkBoxMap[parentId].checked = CheckboxState.Unchecked;
          } else {
          // otherwise the parent is indeterminate (cannot be checked)
            checkBoxMap[parentId].checked = CheckboxState.Indeterminate;
          }
          break;
        case CheckboxState.Indeterminate:
        // if the item is indeterminate the parent HAS to be indeterminate
        // (the parent can have no other state), this is not a clicked state
        // typically this will happen on the recursive pass
          checkBoxMap[parentId].checked = CheckboxState.Indeterminate;
          break;
        default:
          break;
      }
    }
    if (checkBoxMap[parentId]?.parentId) {
      return upstreamTreeCheck(
        checkBoxMap[parentId],
        checkBoxMap
      );
    }
  }
  return checkBoxMap;
}

/**
 * @downstreamTreeCheck
 *
 * Handles all downstream (children/subAccounts) tree checking for an account map and updates
 * the appropriate checkbox state based on the state of any of the parents. Called
 * recursively depending on how many levels of children there are.
 *
 * @param string
 * @param CheckboxState
 * @param AccountHashMapKeyType
 *
 * ```typescript
 *   import {downstreamTreeCheck} from "@ion/core/src/entities/Account";
 *
 *   downstreamTreeCheck('1234', CheckboxState.Unchecked, checkboxMap);
 *
 * ```
 */
export function downstreamTreeCheck(
  itemId: string,
  checkedState: CheckboxState,
  checkBoxMap: AccountHashMapKeyType
) {
  const subAccounts = checkBoxMap[itemId]?.subAccounts;
  if (subAccounts?.length) {
    subAccounts.forEach(account => {
      checkBoxMap[account].checked = checkedState;
      downstreamTreeCheck(account, checkedState, checkBoxMap);
    });
  }
  return checkBoxMap;
}

/**
 * @flattenAccountTree
 *
 * Handles flattening of one or more account lists into a single
 * last that includes sub accounts. Values and labels are added to
 * each entry as well as a disabled state based on the account's status.
 *
 * @param {Array<object>} accountList
 * @param {Array<object>} secondaryAccountList
 *
 * ```typescript
 *   import {flattenAccountTree} from "@ion/core/src/entities/Account";
 *
 *   flattenAccountTree(accountList, secondaryAccountList);
 *
 * ```
 */
export function flattenAccountTree(
  accountList: Account[],
  includeSubAccounts = true
): Account[] {
  const flattenedTree = [];

  function setTreeItem(account) {
    flattenedTree.push(account);
  };

  function checkSubAccount(parentAccount) {
    parentAccount.subAccounts.forEach(sub => {
      const sanitizedAccount = sanitizeAccount(sub);
      setTreeItem(sanitizedAccount);
      if (sanitizedAccount?.subAccounts) {
        checkSubAccount(sanitizedAccount);
      }
    });
  };

  accountList.forEach(account => {
    setTreeItem(account);
    if (includeSubAccounts && account.subAccounts) {
      checkSubAccount(account);
    }
  });

  return flattenedTree;
}

/**
 * Determines whether the given collection contains at least one valid
 * account id, used to avoid issuing geofence queries with an empty
 * `landmarkAccountId IN ()` clause.
 *
 * ```typescript
 *   import {hasAccountIds} from '@ion/core/src/entities/Accounts';
 *   ...
 *
 *   // skips the request until the account hierarchy is loaded...
 *   if (hasAccountIds(accountIds)) {
 *     loadGeofences({accountIds});
 *   }
 * ```
 *
 * @param accountIds  the account ids to validate
 * @returns           `true` when at least one account id is present, otherwise `false`
 */
export const hasAccountIds = (accountIds: number[]): boolean => (
  Boolean(accountIds?.some?.(Boolean))
);

/**
 * Returns all account ids for the authenticated user account hierarchy.
 *
 * ```typescript
 *     import {getAccountIds} from '@ion/dls/src/maps/helpers';
 *
 *     const accountId = getAccountIds(user, accounts);
 *
 * ```
 */
export const getAccountIds = (user: User, accounts: Account[]): number[] => {
  const accountIds = [];
  const id         = entityId(user?.account);
  accounts?.forEach(account => {
    const id = entityId(account?.href);
    accountIds.push(id);
    if (account?.subAccounts) {
      getSubAccount(account, accountIds);
    }
  });
  if (id) {
    accountIds?.unshift(id);
  }
  return uniq(accountIds);
};

/**
 * Provides a utility for getting all subaccount ids for the given account id.
 *
 * @private
 */
export const getSubAccount = (parentAccount: GenericObject, accountIds: number[]): number[] => {
  parentAccount.subAccounts?.forEach(sub => {
    const sanitizedAccount = sanitizeAccount(sub);
    const id = entityId(sanitizedAccount?.href);
    accountIds.push(id);
    if (sanitizedAccount?.subAccounts) {
      getSubAccount(sanitizedAccount, accountIds);
    }
  });
  return accountIds;
};

/**
 * Finds a matching account from a nested account list by comparing it
 * to the currently selected account.
 *
 * @param accounts All accounts within a given account hierarchy.
 * @param currentAccount The currently selected account to be located.
 */
export const findCurrentAccountMatch = (accounts, currentAccount) => {
  const target = sanitizeAccount(currentAccount);
  const stack  = accounts?.slice();
  const {href} = target;
  const match  = [];
  while (stack.length) {
    const normalized = sanitizeAccount(stack.pop());
    if (normalized.href !== href) {
      const {subAccounts} = normalized;
      if (subAccounts?.length) {
        stack.push(...subAccounts);
      }
    } else {
      match.push(normalized);
    }
  }
  return match;
};

/**
 * @getParentAccounts
 *
 * Recursive climbing of an account list to find all its parents
 * in a string lost by the identifier
 *
 * @param account account from which to find all its parents
 * @param accountList the account list from which to search
 *   for the matches to the passed account
 * @param identifier the account property to identify the value of each
 *   parent that will be added to the returned array
 */
export const getParentAccounts = (
  account: AccountLink,
  accountList: AccountLink[],
  identifier = 'title'
): string[] => {
  const accountTree = setAccountTree(accountList, true);
  return getAccountLineage(entityId(account), accountTree, identifier);
};

/**
 * @sanitizeAccount
 *
 * Account utility for scrubbing an account object that may contain
 * the ctc property 'subaccount'
 *
 * @param account account object
 */
export const sanitizeAccount = (account: SubAccountLink): AccountLink => {
  return account?.subaccount || account;
};

/**
 * @getAccountLineage
 *
 * Recursive climbing of an flattened account list to find provided account id's parents
 * in a string as per requested identifier
 *
 * @param accountId accountId for which to find all its parents
 * @param flattenedAccountTree the flattened account list with each account id as key
 * @param identifier the account property to identify the value of each
 *   parent that will be added to the returned array
 */
export const getAccountLineage = (
  accountId: number,
  flattenedAccountTree,
  identifier = 'title'
): string[] => {
  const accounts = [];
  function findParent(acct) {
    accounts.push(acct[identifier]);
    const parentId = entityId(acct.parentId);
    if (flattenedAccountTree[parentId]) {
      findParent(flattenedAccountTree[parentId]);
    }
  };
  flattenedAccountTree[accountId] && findParent(flattenedAccountTree[accountId]);
  return accounts;
};
